import { css } from '@emotion/css';
import { useState } from 'react';

import { t } from '@grafana/i18n';
import { IconButton, Stack, useStyles2 } from '@grafana/ui';
import { useQueryLibraryContext } from 'app/features/explore/QueryLibrary/QueryLibraryContext';
import { type SavedQuery } from 'app/features/explore/QueryLibrary/types';

import { DeleteSavedQueriesModal } from '../DeleteSavedQueriesModal';
import { QueryLibraryInteractions } from '../QueryLibraryAnalyticsEvents';
import { canEditQuery, hasWritePermissions } from '../utils/identity';

type Props = {
  query: SavedQuery;
  disabled?: boolean;
  onDelete?: () => void;
  onDuplicate?: () => void;
};

// Duplicate and Delete icon buttons shown in the details panel footer. Delete triggers a confirmation modal.
// The ConfirmModal is rendered locally (not via ShowConfirmModalEvent) to avoid click-through closing the
// parent SavedQueriesModal when Cancel is clicked.
export function SavedQueryDetailActions({ query, disabled, onDelete, onDuplicate: onDuplicateQuery }: Props) {
  const styles = useStyles2(getStyles);
  const { setNewQuery, setIsEditingQuery, triggerAnalyticsEvent } = useQueryLibraryContext();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const onDuplicate = () => {
    triggerAnalyticsEvent(QueryLibraryInteractions.duplicateQueryClicked);
    onDuplicateQuery?.();
    setNewQuery({
      ...query,
      uid: undefined,
      title: t('saved-queries.details.default-title', 'New query'),
    });

    setIsEditingQuery(true);
  };

  return (
    <>
      <Stack direction="row" gap={1}>
        <IconButton
          aria-label={t('saved-queries.details.duplicate', 'Duplicate')}
          tooltip={t('saved-queries.details.duplicate', 'Duplicate')}
          name="copy"
          variant="secondary"
          size="md"
          // A row that failed to load can't be duplicated (its datasource metadata is missing), but it can
          // still be deleted.
          disabled={disabled || !hasWritePermissions() || Boolean(query.loadError)}
          onClick={onDuplicate}
        />
        <IconButton
          aria-label={t('saved-queries.details.delete', 'Delete')}
          tooltip={t('saved-queries.details.delete', 'Delete')}
          name="trash-alt"
          size="md"
          disabled={disabled || !canEditQuery(query)}
          onClick={() => setIsDeleteConfirmOpen(true)}
        />
      </Stack>
      <DeleteSavedQueriesModal
        isOpen={isDeleteConfirmOpen}
        uids={query.uid ? [query.uid] : []}
        modalClass={styles.centeredModal}
        onDismiss={() => setIsDeleteConfirmOpen(false)}
        onDeleted={(_deletedUids, allDeleted) => {
          // Single query: only close and run cleanup once it actually deleted; keep the modal open on
          // failure so the user can retry.
          if (!allDeleted) {
            return;
          }
          setIsDeleteConfirmOpen(false);
          triggerAnalyticsEvent(QueryLibraryInteractions.deleteQueryClicked);
          onDelete?.();
        }}
      />
    </>
  );
}

const getStyles = () => ({
  centeredModal: css({
    top: '50%',
    transform: 'translateY(-50%)',
  }),
});
